import React, { useState, useEffect, useRef } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { 
  faHome, 
  faUsers,
  faBars,
  faChevronDown,
  faTimes,
  faFilePen,
  faWrench,
  faBoxOpen,
  faFileInvoice,
  faCalendarAlt,
  faShieldHalved,
  faCar,
  faBookOpen
} from '@fortawesome/free-solid-svg-icons'
import { useAuth } from '@/contexts/AuthContext'

interface SidebarProps {
  isCollapsed: boolean
  onToggle: () => void
}

interface NavigationItem {
  name: string
  href: string
  icon: any
  children?: NavigationItem[]
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation()
  const [openDropdowns, setOpenDropdowns] = useState<Set<string>>(new Set())
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const { currentUser, canAccessFeature } = useAuth()
  const [allowedRoutes, setAllowedRoutes] = useState<string[]>([])

  useEffect(() => {
    (async () => {
      if (!currentUser?.uid) { setAllowedRoutes([]); return }
      try {
        setAllowedRoutes(['*'])
      } catch {
        setAllowedRoutes([])
      }
    })()
  }, [currentUser?.uid])

  // Map navigation keys to feature keys
  const featureKeyForNav: Record<string, string> = {
    'service-requests': 'service_requests',
    'audit-log': 'audit_logs',
    // shop-settings visibility should be permission-based; no feature gate
    // users and roles visibility is permission-driven; skip feature gating
    'service-templates': 'templates',
    'inventory': 'inventory',
    'invoices': 'invoices',
    'calendar': 'calendar',
    'customers': 'customers',
    'documentation': 'docs',
  }
  const can = (key: string) => {
    // Shop Settings and Users/Roles: rely only on permissions (skip feature gating)
    if (key === 'shop-settings' || key === 'users' || key === 'roles') {
      return allowedRoutes.includes('*') || allowedRoutes.includes(key)
    }
    const featureKey = featureKeyForNav[key] || key
    return (allowedRoutes.includes('*') || allowedRoutes.includes(key)) && canAccessFeature(featureKey)
  }

  // const settingsChildren: NavigationItem[] = []

  const navigation: NavigationItem[] = [
    ...(can('dashboard') ? [{ name: 'Dashboard', href: '/', icon: faHome }] : []),
    ...(can('service-requests') ? [{ name: 'Service Requests', href: '/service-requests', icon: faFilePen }] : []),
    ...(can('invoices') ? [{ name: 'Invoices', href: '/invoices', icon: faFileInvoice }] : []),
    ...(can('calendar') ? [{ name: 'Calendar', href: '/calendar', icon: faCalendarAlt }] : []),
    ...(can('inventory') ? [{ name: 'Inventory', href: '/inventory', icon: faBoxOpen }] : []),
    ...(can('customers') ? [{ name: 'Customers', href: '/customers', icon: faUsers }] : []),
    ...(can('vehicles') ? [{ name: 'Vehicles', href: '/vehicles', icon: faCar }] : []),
    ...(can('documentation') ? [{ name: 'Documentation', href: '/documentation', icon: faBookOpen }] : []),
    // Settings dropdown
    {
      name: 'Settings',
      href: '#',
      icon: faWrench,
      children: [
        ...((allowedRoutes.includes('*') || allowedRoutes.includes('shop-settings')) ? [{ name: 'Shop Settings', href: '/shop-settings', icon: faWrench }] : []),
        ...(can('inventory') ? [{ name: 'Inventory Settings', href: '/inventory-settings', icon: faBoxOpen }] : []),
        ...(can('docs') ? [{ name: 'Documentation Settings', href: '/documentation-settings', icon: faBookOpen }] : []),
        ...(can('templates') ? [{ name: 'Service Templates', href: '/service-templates', icon: faFilePen }] : []),
        ...(allowedRoutes.includes('*') || allowedRoutes.includes('users') ? [{ name: 'Users', href: '/users', icon: faUsers }] : []),
        // Roles page should also respect feature gating; hide if roles feature disabled
        ...((allowedRoutes.includes('*') || allowedRoutes.includes('roles')) && canAccessFeature('roles') ? [{ name: 'Roles & Permissions', href: '/roles', icon: faShieldHalved }] : []),
        ...(can('audit-log') ? [{ name: 'Audit Log', href: '/audit-log', icon: faFilePen }] : []),
      ]
    }
  ]

  // Close hover states when location changes (but keep dropdowns open)
  useEffect(() => {
    // Use setTimeout to avoid setState in effect
    const timeoutId = setTimeout(() => setHoveredItem(null), 0)
    return () => clearTimeout(timeoutId)
  }, [location.pathname])

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current)
      }
    }
  }, [])

  const toggleDropdown = (itemName: string) => {
    const newOpenDropdowns = new Set(openDropdowns)
    if (newOpenDropdowns.has(itemName)) {
      newOpenDropdowns.delete(itemName)
    } else {
      newOpenDropdowns.add(itemName)
    }
    setOpenDropdowns(newOpenDropdowns)
  }

  const isItemActive = (item: NavigationItem): boolean => {
    if (item.href === location.pathname) return true
    if (item.children) {
      return item.children.some(child => child.href === location.pathname)
    }
    return false
  }

  const handleMouseEnter = (itemName: string) => {
    // Clear any existing timeout
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = null
    }
    setHoveredItem(itemName)
  }

  const handleMouseLeave = () => {
    // Set a timeout to close the menu after 1 second
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null)
    }, 1000)
  }

  const renderNavigationItem = (item: NavigationItem) => {
    const isActive = isItemActive(item)
    const isDropdownOpen = openDropdowns.has(item.name)
    const hasChildren = item.children && item.children.length > 0

    if (isCollapsed) {
      // Collapsed view - icon only with hover mini menu for dropdowns
      return (
        <div
          key={item.name}
          className="relative"
        >
          {hasChildren ? (
            // Dropdown item in collapsed mode
            <div
              className="relative group"
              onMouseEnter={() => handleMouseEnter(item.name)}
              onMouseLeave={handleMouseLeave}
            >
              {/* Extended hover area */}
              <div className="absolute inset-0 z-10" />

              <button
                className={`flex items-center justify-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
                title={item.name}
              >
                 <FontAwesomeIcon
                   icon={item.icon}
                   className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                   }`}
                 />
              </button>

              {/* Mini menu for collapsed dropdowns */}
              {hasChildren && hoveredItem === item.name && item.children && (
                <div
                  className="absolute left-full top-0 ml-2 w-48 bg-popover rounded-lg shadow-lg border border-border py-2 z-50"
                  onMouseEnter={() => handleMouseEnter(item.name)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.href
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`flex items-center px-4 py-2 text-sm font-medium transition-colors ${isChildActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                      >
                        <FontAwesomeIcon
                          icon={child.icon}
                          className={`mr-3 h-4 w-4 ${isChildActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                        />
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            // Regular item in collapsed mode
            <Link
              to={item.href}
              className={`flex items-center justify-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
              title={item.name}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
            </Link>
          )}
        </div>
      )
    } else {
      // Expanded view - full width with text
      return (
        <div key={item.name}>
          {hasChildren ? (
            // Dropdown item in expanded mode
            <div>
              <button
                onClick={() => toggleDropdown(item.name)}
                className={`flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                  ? 'bg-accent text-accent-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                }`}
              >
                <div className="flex items-center">
                  <FontAwesomeIcon
                    icon={item.icon}
                    className={`mr-3 h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                    }`}
                  />
                  {item.name}
                </div>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`h-4 w-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && item.children && (
                <div className="mt-1 ml-4 space-y-1">
                  {item.children.map((child) => {
                    const isChildActive = location.pathname === child.href
                    return (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isChildActive
                          ? 'bg-accent text-accent-foreground'
                          : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}

                      >
                        <FontAwesomeIcon
                          icon={child.icon}
                          className={`mr-3 h-4 w-4 ${isChildActive ? 'text-primary' : 'text-muted-foreground'
                          }`}
                        />
                        {child.name}
                      </Link>
                    )
                  })}
                </div>
              )}
            </div>
          ) : (
            // Regular item in expanded mode
            <Link
              to={item.href}
              className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive
                ? 'bg-accent text-accent-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`mr-3 h-5 w-5 ${isActive ? 'text-primary' : 'text-muted-foreground'
                }`}
              />
              {item.name}
            </Link>
          )}
        </div>
      )
    }
  }

  return (
    <>
      {/* Desktop Sidebar */}
      <div className={`hidden md:flex fixed left-0 top-0 h-screen bg-background shadow-sm border-r border-border transition-all duration-300 z-30 ${isCollapsed ? 'w-16' : 'w-64'
        }`}>
        <div className="flex flex-col w-full">
          {/* Header */}
          <div className="flex items-center justify-between h-16 px-4 border-border">
            {/*!isCollapsed && (
              <Link to="/" className="flex items-center">
                <h1 className="text-xl font-bold text-primary">{config?.name || 'Nova'}</h1>
              </Link>
            )*/}
            <button
              onClick={onToggle}
              className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
            >
              <FontAwesomeIcon icon={faBars} className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navigation.map(renderNavigationItem)}
          </nav>

          {/* Footer */}
          {!isCollapsed && (
            <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
              <span>
                Powered by <span className="font-semibold text-primary">Motiv</span>
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div className="md:hidden">
        {/* Mobile Toggle Button */}
        <button
          onClick={onToggle}
          className="fixed top-3 left-4 z-50 p-2 bg-background rounded-lg shadow-lg border border-border"
        >
          <FontAwesomeIcon icon={faBars} className="h-6 w-6 text-foreground" />
        </button>

        {/* Mobile Sidebar */}
        {!isCollapsed && (
          <>
            {/* Overlay */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={onToggle}
            />

            {/* Sidebar */}
            <div className="fixed left-0 top-0 h-full w-auto bg-background shadow-lg z-50">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between h-16 px-4 border-b border-border">
                  {/*<Link to="/" className="flex items-center" onClick={onToggle}>
                    <h1 className="text-xl font-bold text-primary">{config?.name || 'Nova'}</h1>
                  </Link>*/}
                  <button
                    onClick={onToggle}
                    className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    <FontAwesomeIcon icon={faTimes} className="h-5 w-5" />
                  </button>
                </div>

                {/* Navigation */}
                <nav className="flex-1 p-4 space-y-2">
                  {navigation.map(renderNavigationItem)}
                </nav>

                {/* Footer */}
                <div className="px-4 py-3 border-t border-border text-xs text-muted-foreground">
                  <span>
                    Powered by <span className="font-semibold text-primary">Motiv</span>
                  </span>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default Sidebar 